const DEFAULT_API_ROOT = 'https://api.certificationiso.com/api';

const normalizeUrl = (value: string) => value.trim().replace(/\/+$/, '');

const resolveApiRoot = (): string => {
  const explicitRoot = process.env.REACT_APP_API_ROOT?.trim();
  if (explicitRoot) {
    return normalizeUrl(explicitRoot);
  }

  const certificateBase = process.env.REACT_APP_API_BASE?.trim();
  if (certificateBase) {
    return normalizeUrl(certificateBase.replace(/\/certificates\/?$/i, ''));
  }

  const authBase = process.env.REACT_APP_AUTH_API_BASE?.trim();
  if (authBase) {
    return normalizeUrl(authBase.replace(/\/auth\/?$/i, ''));
  }

  return DEFAULT_API_ROOT;
};

const API_ROOT = resolveApiRoot();

const resolveApiBase = (value: string | undefined, fallbackPath: string) =>
  normalizeUrl((value ?? '').trim() || `${API_ROOT}${fallbackPath}`);

export const CERTIFICATE_API_BASE = resolveApiBase(
  process.env.REACT_APP_API_BASE,
  '/certificates'
);

export const AUTH_API_BASE = resolveApiBase(process.env.REACT_APP_AUTH_API_BASE, '/auth');

export const INQUIRIES_API_BASE = resolveApiBase(
  process.env.REACT_APP_INQUIRIES_API_BASE,
  '/inquiries'
);

export const APP_DOMAIN = process.env.REACT_APP_DOMAIN?.trim() || 'certificationiso.com';

export const parseApiResponse = async <T>(response: Response): Promise<T | { message?: string } | null> => {
  const text = await response.text();

  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text) as T;
  } catch {
    return { message: text };
  }
};

export const getApiErrorMessage = (payload: unknown, fallback: string): string => {
  if (payload && typeof payload === 'object') {
    const response = payload as {
      message?: unknown;
      error?: unknown;
      errors?: Record<string, unknown> | unknown[];
      title?: unknown;
    };

    if (typeof response.message === 'string' && response.message.trim()) {
      return response.message;
    }

    if (typeof response.error === 'string' && response.error.trim()) {
      return response.error;
    }

    if (response.errors) {
      if (Array.isArray(response.errors) && response.errors.length > 0 && typeof response.errors[0] === 'string') {
        return response.errors[0];
      }

      if (typeof response.errors === 'object') {
        const errorValues = Object.values(response.errors);
        if (errorValues.length > 0) {
          const firstVal = errorValues[0];
          if (Array.isArray(firstVal) && firstVal.length > 0 && typeof firstVal[0] === 'string') {
            return firstVal[0];
          }
          if (typeof firstVal === 'string') {
            return firstVal;
          }
        }
      }
    }

    if (typeof response.title === 'string' && response.title.trim()) {
      return response.title;
    }
  }

  return fallback;
};

